'use client'

import dynamic from 'next/dynamic'
import { alpha } from '@mui/material/styles'
import { Box, Card, CardMedia, CardContent, CardActions, Typography, Dialog, Grid } from '@mui/material'

// Types
import type { DialogProps } from '@/types/components/molecules/dialog'

// Utils
import ToPersianNumber from '@/utils/number/to-persian-number'

// Components
const PrimaryButtonAtom = dynamic(() => import('@/components/atoms/button/primary'))
const IconButtonAtom = dynamic(() => import('@/components/atoms/button/icon'))
const CloseTwoIconAtom = dynamic(() => import('@/components/atoms/icon/two-tone/close2'))
const NoImageIconAtom = dynamic(() => import('@/components/atoms/icon/two-tone/no-image'))

const DialogMolecule = (props: DialogProps) => {
	// Props
	const { open, loading, title, prices, description, image, setOpen, action } = props

	return (
		<Dialog
			open={open}
			onClose={() => setOpen(false)}
			slotProps={{
				backdrop: { sx: { bgcolor: alpha('#a1a1a1ff', 0.33) } },
				paper: {
					elevation: 0,
					sx: {
						p: 0,
						borderRadius: 3,
						height: '80dvh',
						width: 640,
						display: 'flex',
						flexDirection: 'column'
					}
				}
			}}
		>
			<Card
				sx={{
					height: 1,
					display: 'flex',
					flexDirection: 'column'
				}}
			>
				{image ? (
					<CardMedia sx={{ minHeight: '350px' }} image={image} title={title} />
				) : (
					<CardMedia
						sx={{
							' & .MuiSvgIcon-root': {
								width: 1,
								height: '40dvh'
							}
						}}
					>
						<NoImageIconAtom />
					</CardMedia>
				)}

				<Box sx={{ position: 'absolute', top: 16, right: 16, backgroundColor: '#f5f5f7', borderRadius: '100%', '& .MuiButtonBase-root': { color: 'grey.700', p: 0 } }}>
					<IconButtonAtom icon={<CloseTwoIconAtom />} size="small" onClick={() => setOpen(false)} />
				</Box>

				<CardContent
					sx={{
						flexGrow: 1,
						overflowY: 'auto',
						px: 2,
						py: 1
					}}
				>
					{title && (
						<Typography
							sx={{
								fontSize: 24,
								fontWeight: 600,
								p: 2
							}}
						>
							{title}
						</Typography>
					)}

					{description && (
						<Box sx={{ px: 0 }}>
							<Typography sx={{ fontSize: 14 }}>{description}</Typography>
						</Box>
					)}
				</CardContent>

				<CardActions sx={{ px: 2 }}>
					<Grid container sx={{ width: 1 }}>
						<Grid size={6}>
							{prices && prices.length > 0 && prices[0].price_discount > 0 ? (
								<Box>
									<Typography sx={{ fontSize: 12, fontWeight: 500, textDecoration: 'line-through', color: 'grey.500' }}>
										{ToPersianNumber(new Intl.NumberFormat().format(prices[0].price / 10).toString())}
									</Typography>

									<Typography sx={{ fontSize: 24, fontWeight: 500 }}>
										{ToPersianNumber(
											new Intl.NumberFormat()
												.format(prices[0].price_discount > 0 ? (prices[0].price - prices[0].price_discount) / 10 : prices[0].price / 10)
												.toString()
										)}
									</Typography>
								</Box>
							) : (
								<Typography sx={{ fontSize: 16, fontWeight: 500, mt: 1 }}>
									{ToPersianNumber(new Intl.NumberFormat().format(prices ? prices[0].price : 0 / 10).toString())}
								</Typography>
							)}
						</Grid>

						<Grid size={6} sx={{ display: 'flex' }}>
							<Grid container spacing={2} sx={{ width: 1, justifyContent: 'space-between' }}>
								<Grid
									size={'grow'}
									sx={{
										p: 0,
										'& .MuiButton-root': { width: 1 }
									}}
								>
									<PrimaryButtonAtom text="+" color="inherit" loading={loading} onClick={action} />
								</Grid>

								<Grid
									size={'grow'}
									sx={{
										p: 0,
										'& .MuiButton-root': { width: 1 }
									}}
								>
									<PrimaryButtonAtom text={'-'} color="inherit" loading={loading} onClick={action} />
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</CardActions>
			</Card>
		</Dialog>
	)
}

export default DialogMolecule
